<template>
    <main :class="[isLight ? 'lightmode' : 'darkmode']">
        <h1>Time calculations</h1>
        <div :class="[isLight ? 'lightmode' : 'darkmode']" role="tablist" aria-label="Viewing options">
            <button role="tab" :aria-selected="selectedTab === 1" aria-controls="conversions-table" id="tab-convert" @click.prevent="selectedTab = 1" :class="[isLight ? 'lightmode' : 'darkmode',selectedTab === 3 ? 'has-divider' : '']">Conversions</button>
            <button role="tab" :aria-selected="selectedTab === 2" aria-controls="calculator" id="tab-calc" @click.prevent="selectedTab = 2" :class="[isLight ? 'lightmode' : 'darkmode',selectedTab === 1 ? 'has-divider' : '']">Calculator</button>
            <button role="tab" :aria-selected="selectedTab === 3" aria-controls="settings" id="tab-settings" @click.prevent="selectedTab = 3" :class="[isLight ? 'lightmode' : 'darkmode']">Settings</button>
        </div>
        <section v-if="selectedTab === 1" role="tabpanel" id="conversions-table" aria-labelledby="tab-convert" aria-describedby="selected-scope-info">
            <p id="selected-scope-info">{{ scopes[selectedScope] }}</p>
            <table :class="[isLight ? 'lightmode' : 'darkmode']">
                <thead>
                    <tr>
                        <th :class="[isLight ? 'lightmode' : 'darkmode']" style="width:20%;">Unit</th>
                        <th :class="[isLight ? 'lightmode' : 'darkmode']">Conversion</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in conversions[selectedScope]" :key="item.unit">
                        <td :class="[isLight ? 'lightmode' : 'darkmode']">{{ item.unit }}</td>
                        <td :class="[isLight ? 'lightmode' : 'darkmode']">{{ item.conversion }}</td>
                    </tr>
                </tbody>
            </table>
        </section>
        <section v-if="selectedTab === 2" role="tabpanel" id="calculator" aria-labelledby="tab-calc" aria-describedby="operation-info">
            <p id="operation-info">{{ operations[selectedType] }}</p>
            <form @submit.prevent="calculate" :class="[isLight ? 'lightmode' : 'darkmode']">
                <div role="group" v-for="(input,selectIndex) in timeInputs" :key="selectIndex" class="input-select">
                    <div v-for="(unit,unitIndex) in input.unitInputs" :key="unit" class="input-unit">
                        <label :for="unit+selectIndex">{{ unit }}</label>
                        <input type="number" :class="[isLight ? 'lightmode' : 'darkmode']" :id="unit+selectIndex" v-model="input.inputValues[unitIndex]" />
                    </div>
                    <div class="select-controls">
                        <select :aria-label="'Units for entry ' + (selectIndex + 1)" class="units-select" :class="[isLight ? 'lightmode' : 'darkmode']" v-if="!syncUnits" v-model="input.selectedSet">
                            <option v-for="(set,index) in units[selectedScope]" :key="index" :value="index">{{ set }}</option>
                        </select>
                        <button class="remove-input" :aria-label="'Remove entry ' + (selectIndex + 1)" :class="[isLight ? 'lightmode' : 'darkmode']" v-if="timeInputs.length > 2" @click="timeInputs.splice(selectIndex,1)" type="button">✖</button>
                    </div>
                </div>
                <button :class="[isLight ? 'lightmode' : 'darkmode']" id="add-input" @click.prevent="timeInputs.push(createTimeInputObject());">+ Add input</button>
                <button :class="[isLight ? 'lightmode' : 'darkmode']" type="submit">Calculate</button>
            </form>
            <fieldset>
                <legend>Result in {{ scopes[selectedScope] }} {{ units[selectedScope][selectedCalcSet] }}</legend>
                <output :class="[formSubmitted ? '' : 'not-submitted',isLight ? 'lightmode' : 'darkmode']">{{ resultLbl }}</output>
            </fieldset>
        </section>
        <section v-if="selectedTab === 3" role="tabpanel" id="settings" aria-labelledby="tab-settings">
            <label for="operation-select">Operation</label>
            <select class="settings-selects" :class="[isLight ? 'lightmode' : 'darkmode']" id="operation-select" v-model="selectedType">
                <option :value="0">Difference</option>
                <option :value="1">Sum</option>
                <option :value="2">Average</option>
                <option :value="3">Lowest</option>
                <option :value="4">Highest</option>
            </select>
            <label for="scope-select">Scope</label>
            <select class="settings-selects" :class="[isLight ? 'lightmode' : 'darkmode']" id="scope-select" v-model="selectedScope">
                <option :value="0">ISO Standard</option>
                <option :value="1">Banking Standard</option>
                <option :value="2">Business</option>
                <option :value="3">Julian Calendar</option>
            </select>
            <div>
                <input type="checkbox" id="syncUnits" v-model="syncUnits" /><label id="syncUnitsLbl" for="syncUnits">Use the same set of units for all inputs and the result</label>
            </div>
            <label v-if="!syncUnits" for="result-unit-select">Show results in following units</label>
            <label v-else for="result-unit-select">Use following units</label>
            <select class="settings-selects" :class="[isLight ? 'lightmode' : 'darkmode']" id="result-unit-select" v-model="selectedCalcSet">
                <option v-for="(unitSet,index) in units[selectedScope]" :key="index" :value="index">{{ unitSet }}</option>
            </select>
        </section>
    </main>
</template>

<script setup>
    import { defineProps,toRef,ref,computed, onMounted,watch, reactive,watchEffect } from 'vue';

    const props = defineProps({
       darkMode:Boolean
    });

    var isLight = toRef(props,'darkMode');
    var formSubmitted = ref(false);
    var resultLbl = ref("Result");
    let selectedTab = ref(2);
    let selectedType = ref(1);
    let selectedScope = ref(0);
    var selectedCalcSet = ref(0);
    let syncUnits = ref(false);

    const conversions = [[{unit:"1 year",conversion:"12 months or 365,2425 days"},{unit:"1 month",conversion:"30,436875 days"},
                          {unit:"1 week",conversion:"7 days"},{unit:"1 day",conversion:"24 hours or 1440 minutes or 86400 seconds"},
                          {unit:"1 hour",conversion:"60 minutes or 3600 seconds"},{unit:"1 minute",conversion:"60 seconds"},{unit:"1 second",conversion:"Base unit"}],
                         [{unit:"1 year",conversion:"12 months or 360 days"},{unit:"1 month",conversion:"30 days"},
                          {unit:"1 week",conversion:"7 days"},{unit:"1 day",conversion:"24 hours or 1440 minutes or 86400 seconds"},
                          {unit:"1 hour",conversion:"60 minutes or 3600 seconds"},{unit:"1 minute",conversion:"60 seconds"},{unit:"1 second",conversion:"Base unit"}],
                         [{unit:"1 year",conversion:"12 months or 260 days"},{unit:"1 month",conversion:"21,66667 days"},
                          {unit:"1 week",conversion:"5 days"},{unit:"1 day",conversion:"8 hours or 480 minutes or 28800 seconds"},
                          {unit:"1 hour",conversion:"60 minutes or 3600 seconds"},{unit:"1 minute",conversion:"60 seconds"},{unit:"1 second",conversion:"Base unit"}],
                         [{unit:"1 year",conversion:"12 months or 365,25 days"},{unit:"1 month",conversion:"30,4375 days"},
                          {unit:"1 week",conversion:"7 days"},{unit:"1 day",conversion:"24 hours or 1440 minutes or 86400 seconds"},
                          {unit:"1 hour",conversion:"60 minutes or 3600 seconds"},{unit:"1 minute",conversion:"60 seconds"},{unit:"1 second",conversion:"Base unit"}],];
    const scopes = ["ISO Standard","Banking standard","Business","Julian"];
    const operations = ["Difference calculator","Sum calculator","Find average","Find lowest","Find highest"];
    const units = [["hours, minutes","hours, minutes, seconds","days, hours, minutes, seconds",
                    "weeks, days","years, months, days","years, months","seconds","minutes",
                    "hours","days","weeks","months","years"],
                   ["hours, minutes","hours, minutes, seconds","days, hours, minutes, seconds",
                    "weeks, days","years, months, days","years, months","seconds","minutes",
                    "hours","days","weeks","months","years"],
                   ["hours, minutes","hours, minutes, seconds","days, hours, minutes, seconds",
                    "weeks, days","years, months, days","years, months","seconds","minutes",
                    "hours","days","weeks","months","years"],
                   ["hours, minutes","hours, minutes, seconds","days, hours, minutes, seconds",
                    "weeks, days","years, months, days","years, months","seconds","minutes",
                    "hours","days","weeks","months","years"]];

                    
    const createTimeInputObject = () => 
    {
        const state = reactive({
            selectedSet: 0,
            unitInputs: [],
            inputValues: []
        });

        let lastUnits = "";

        watchEffect(() => {
            const scope = selectedScope.value;
            const setIndex = syncUnits.value ? selectedCalcSet.value : state.selectedSet;
            const unitString = units[scope][setIndex];
            const newUnits = units[scope][setIndex].split(", ");
            state.unitInputs = newUnits;
            if (unitString !== lastUnits) 
            {
                state.inputValues = newUnits.map(() => "");
                lastUnits = unitString;
            }
        });

        return state;
    };

    const timeInputs = reactive([]);

    timeInputs.push(createTimeInputObject());
    timeInputs.push(createTimeInputObject());

    const parseToBaseUnit = () =>
    {
        let secondsInDay,secondsInHour,secondsInMinute,secondsInYear,secondsInMonth,secondsInWeek;
        let seconds = [];

        switch(selectedScope.value)
        {
            case 0:
            {
                secondsInYear = 365.2425*86400;
                secondsInMonth = (365.2425/12)*86400;
                secondsInDay = 86400;
                secondsInWeek = 7*86400;
                secondsInHour = 3600;
                secondsInMinute = 60;
                break;
            }

            case 1:
            {
                secondsInYear = 360*86400;
                secondsInMonth = 30*86400;
                secondsInDay = 86400;
                secondsInWeek = 7*86400;
                secondsInHour = 3600;
                secondsInMinute = 60;
                break;
            }

            case 2:
            {
                secondsInHour = 3600;
                secondsInMinute = 60;
                secondsInDay = 8*60;
                secondsInWeek = 5*8*60;
                secondsInMonth = 21.7*8*60;
                secondsInYear = 12*21.66666*8*60;
                break;
            }

            case 3:
            {
                secondsInDay = 86400;
                secondsInHour = 3600;
                secondsInMinute = 60;
                secondsInWeek = 7*86400;
                secondsInYear = 365.25*86400;
                secondsInMonth = (365.25/12)*86400;
                break;
            }
        }

        const multipliers = 
        {
            "years": secondsInYear,
            "months":secondsInMonth,
            "weeks":secondsInWeek,
            "days": secondsInDay,
            "hours": secondsInHour,
            "minutes": secondsInMinute,
            "seconds": 1
        };

        seconds = timeInputs.map(item =>
        {
            return item.unitInputs.reduce((acc,label,index) =>
            {
                const mul = multipliers[label] || 0,val = parseFloat(item.inputValues[index]) || 0;
                return acc+(val*mul);
            },0);
        });

        return seconds;
    }

    const parseToOutputUnits = (seconds) =>
    {
        let secondsInDay,secondsInHour,secondsInMinute,secondsInYear,secondsInMonth,secondsInWeek;
        const isNegative = seconds<0;

        switch(selectedScope.value)
        {
            case 0:
            {
                secondsInYear = 365.2425*86400;
                secondsInMonth = (365.2425/12)*86400;
                secondsInDay = 86400;
                secondsInWeek = 7*86400;
                secondsInHour = 3600;
                secondsInMinute = 60;
                break;
            }

            case 1:
            {
                secondsInYear = 360*86400;
                secondsInMonth = 30*86400;
                secondsInDay = 86400;
                secondsInWeek = 7*86400;
                secondsInHour = 3600;
                secondsInMinute = 60;
                break;
            }

            case 2:
            {
                secondsInHour = 3600;
                secondsInMinute = 60;
                secondsInDay = 8*60;
                secondsInWeek = 5*8*60;
                secondsInMonth = 21.7*8*60;
                secondsInYear = 12*21.7*8*60;
                break;
            }

            case 3:
            {
                secondsInDay = 86400;
                secondsInHour = 3600;
                secondsInMinute = 60;
                secondsInWeek = 7*86400;
                secondsInYear = 365.25*86400;
                secondsInMonth = (365.25/12)*86400;
                break;
            }
        }

        const multipliers = 
        {
            "years": secondsInYear,
            "months":secondsInMonth,
            "weeks":secondsInWeek,
            "days": secondsInDay,
            "hours": secondsInHour,
            "minutes": secondsInMinute,
            "seconds": 1
        };

        const margins = 
        {
            years: 0.0007, // 0.07%
            months: 0.025  // 2.5%
        };

        const activeUnits = units[selectedScope.value][selectedCalcSet.value].split(", ");
        let remainder = Math.abs(seconds);

        return activeUnits.map(unit =>
        {
            const div = multipliers[unit];
            let val = 0;

            if(unit === "years" || unit === "months")
            {
                const rawVal = remainder/div;
                const nearestWhole = Math.round(rawVal);
                const diff = Math.abs(rawVal-nearestWhole);
                const margin = margins[unit];

                if(diff <= margin && nearestWhole > 0)
                {
                    val = nearestWhole;
                    remainder = val*div;
                }

                else
                {
                    val = Math.floor(rawVal);
                }
            }

            else
            {
                val = Math.floor(remainder/div);
            }

            remainder -= val*div;
            
            return {
                label:unit,
                value:isNegative ? -val : val
            };
        });
    }

    const formatTimeOutput = (timeOutput) =>
    {
        const isNegative = timeOutput.some(item => item.value < 0);

        if(isNegative)
        {
            timeOutput = timeOutput.map(item => ({
                ...item,
                value:Math.abs(item.value)
            }));

            const timeStringArr = timeOutput.map(item => item.value + " " + item.label);

            return "- " + timeStringArr.join(",");
        }

        else
        {
            const timeStringArr = timeOutput.map(item => item.value + " " + item.label);

            return timeStringArr.join(",");
        }
    }

    //form submission happens here
    const calculate = () => 
    {
        const seconds = parseToBaseUnit();
        let totalSeconds;

        switch(selectedType.value)
        {
            case 0:
            {
                totalSeconds = seconds.reduce((acc,val) => acc-val);
                break;
            }

            case 1:
            {
                totalSeconds = seconds.reduce((acc,val) => acc+val,0);
                break;
            }

            case 2:
            {
                totalSeconds = seconds.reduce((acc,val) => acc+val,0);
                totalSeconds /= seconds.length;
                break;
            }

            case 3:
            {
                totalSeconds = Math.min(...seconds);
                break;
            }

            case 4:
            {
                totalSeconds = Math.max(...seconds);
                break;
            }
        }
        
        const timeOutput = parseToOutputUnits(totalSeconds);

        resultLbl.value = formatTimeOutput(timeOutput);

        formSubmitted.value = true;
    }

    watch(selectedScope,() =>{selectedCalcSet.value = 0;});
</script>

<style scoped>
    main
    {
        grid-area: 2 / 1 / 3 / 2;
        font-family: Syne;
        display:flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        justify-self:center;
        flex-grow: 1;
        width:92%;
        height:fit-content;
        max-width: 600px;
        padding:1.333% 2%;
        margin:14px auto;
        border-radius: 12px;
    }

    main.lightmode
    {
        background-color: #F7F7F6;
        color:#2b3133;
        box-shadow:#2b3133 0px 1px 7px 1px;
        -webkit-box-shadow:#2b3133 0px 1px 7px 1px;
    }

    main.darkmode
    {
        background-color: #2b3133;
        color: #F7F7F6;
        box-shadow:rgb(50, 142, 110) 0px 1px 7px 1px;
        -webkit-box-shadow:rgb(50, 142, 110) 0px 1px 7px 1px;
    }

    section
    {
        display:flex;
        flex-direction: column;
        gap:5px;
        padding:2px 7px 0;
    }

    section#calculator
    {
        width:100%;
        align-items: center;
    }

    form
    {
        display:flex;
        flex-direction: column;
        gap:8px;
        align-items: center;
    }

    table
    {
        font-size: clamp(12px,4.5vw,30px);
    }

    table,th,td
    {
        border:1px solid;
        border-collapse: collapse;
    }

    table.lightmode,th.lightmode,td.lightmode
    {
        border-color: #2b3133;
    }

    table.darkmode,th.darkmode,td.darkmode
    {
        border-color: #F7F7F6;
    }

    h1
    {
        margin:0;
        padding:0;
        cursor:default;
        font-size:x-large;
    }

    h1.lightmode
    {
        color:#2b3133;
    }

    h1.darkmode
    {
        color: #F7F7F6;
    }

    p
    {
        margin:0;
        padding:0;
        font-weight: bold;
        font-size: larger;
        align-self: center;
    }

    div[role=tablist]
    {
        width:100%;
        display: flex;
        flex-direction: row;
        justify-content: stretch;
        align-items: center;
    }

    div[role=tablist].lightmode
    {
        border-bottom:1px solid #2b3133;
    }

    div[role=tablist].darkmode
    {
        border-bottom:1px solid #F7F7F6;
    }

    button[role=tab]
    {
        flex:1;
        min-width: 0;
        font-size: clamp(12px,5.5vw,24px);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        overflow-x: hidden;
        text-overflow: ellipsis;
    }

    button[role=tab].lightmode
    {
        color: #2b3133;
    }

    button[role=tab].darkmode
    {
        background-color: #2b3133;
        color: #F7F7F6;
    }

    button[role=tab].darkmode:hover,button[role=tab].lightmode:hover,button[role=tab][aria-selected=true]
    {
        background-color: #90999a;
    }

    button[role=tab].has-divider {position: relative;}

    button[role=tab].has-divider::after 
    {
        content: "";
        position: absolute;
        
        right: 0;
        top: 25%;
        
        width: 1px;
        height: 50%;
        
        background-color: #687174;
    }

    label
    {
        cursor: default;
        user-select: none;
        font-size: clamp(6px,4vw,16px);
    }

    button
    {
        font-family: Syne;
        border-radius:8px;
        border:none;
        cursor:pointer;
    }

    button[type=submit]
    {
        font-size: clamp(20px,15vw,40px);
        padding:4px;
    }

    button[type=submit].lightmode
    {
        background-color: #1DCD9F;
        color:#F7F7F6;
    }

    button[type=submit].darkmode
    {
        background-color: #328E6E;
        color:#F7F7F6;
    }

    button[type=submit]:hover
    {
        text-decoration: underline;
    }

    button#add-input
    {
        font-size:clamp(12px,5vw,24px);
        width:40%;
        padding:5px;
        border-radius:24px;
        border:0.67vw dashed;
        background: none;
    }

    button#add-input.lightmode
    {
        color:#2b3133;
        border-color: #2b3133;
    }

    button#add-input.darkmode
    {
        color:#F7F7F6;
        border-color: #F7F7F6;
    }

    button#add-input:hover
    {
        background: #90999a74;
    }

    button.remove-input
    {
        background: none;
        border:none;
        font-size:clamp(10px,6vw,24px);
    }

    button.remove-input.lightmode
    {
        color:#f82229;
    }

    button.remove-input.darkmode
    {
        color:#fc5e63;
    }

    input[type=number]
    {
        width:80%;
        font-size:x-large;
        appearance: textfield;
        -moz-appearance:textfield;
        font-family:Syne;
        margin:0;
        padding:0;
        text-indent: 5px;
        align-self: center;
        border-radius:4px;
        border:1px solid;
    }

    input[type=number].used-input
    {
        border:2px solid;
    }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button
    {
        appearance: none;
        -webkit-appearance: none;
    }

    input[type=number].lightmode
    {
        background-color: #F7F7F6;
        color: #2b3133;
    }

    input[type=number].darkmode
    {
        background-color: #202526;
        color: #F7F7F6;
    }

    select
    {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        border:none;
        background-repeat: no-repeat;
        background-position: right 0 center;
        background-size: 1em; 
        padding-right: 1.5em;
        cursor:pointer;
    }

    select:focus
    {
        outline:none;
    }

    select.lightmode
    {
        background-color: #F7F7F6;
        color:#2b3133;
        background-image: url('../../public/arrow_lightmode.png');
    }

    select.darkmode
    {
        background-color: #2b3133;
        color:#F7F7F6;
        background-image: url('../../public/arrow_darkmode.png');
    }

    select.settings-selects
    {
        width:fit-content;
        max-width: 90vw;
        font-size: clamp(16px,6vw,24px);
    }

    select.settings-selects.lightmode
    {
        border-bottom: 1px solid #2b3133;
    }

    select.settings-selects.darkmode
    {
        border-bottom: 1px solid #F7F7F6;
    }

    label#syncUnitsLbl,select
    {
        font-size: clamp(10px,6vw,24px);
    }

    div.input-select
    {
        display: flex;
        flex-direction: row;
        width:94%;
        justify-content: space-between;
        align-items: stretch;
    }

    div.select-controls
    {
        display:flex;
        width:15%;
        justify-content: space-between;
    }

    div.input-unit
    {
        display: flex;
        flex-direction: column;
        flex:1;
        max-width: 120px;
        align-items: center;
    }

    div.select-controls select
    {
        width:40%;
        max-width: 100%;
    }

    fieldset
    {
        height:100px;
        border-radius: 8px;
        width:90%;
    }

    legend
    {
        font-size: clamp(10px,4vw,16px);
    }

    output
    {
        font-family: Syne;
        font-size:x-large;
        font-weight: bolder;
        margin:2vw 4vw;
    }

    output.not-submitted
    {
        user-select: none;
    }

    output.not-submitted.lightmode,output.darkmode
    {
        color:#F7F7F6;
    }

    output.not-submitted.darkmode,output.lightmode
    {
        color:#2b3133;
    }

    @media (max-width:260px)
    {
        h1
        {
            font-size:medium;
        }
    }

    @media (min-width:520px)
    {
        button#add-input
        {
            border:3px dashed;
        }
    }
</style>